/**
 * FreeTierBadge — ЕДИНЫЙ индикатор бесплатного тира модели (решение
 * владельца 2026-08-07: один компонент, через него прогоняются данные,
 * условия и отображение).
 *
 * Показывает остаток бесплатных генераций на сегодня и время сброса:
 *   «Бесплатно сегодня: 3 из 10 · обновится в 03:00» (зелёный) или
 *   «Бесплатные на сегодня закончились · обновятся в 03:00» (приглушённый).
 * Тира нет (нет подписки / нет строки-лимита у модели) — не рендерится вовсе.
 *
 * Данные — GET /chat/model-pricing (has_free/free_limit/free_remaining/
 * free_resets_at): per-модельные лимиты tariff_model_free_limits, те же,
 * которыми money-path решает бесплатность списания. Легаси-пул чата тут
 * сознательно не показывается (он общий на все модели — подпись врала бы).
 *
 * Режимы питания:
 *   • сам грузит по (provider, model, type, params) — виджеты image/video;
 *   • готовый `data` — чат уже держит model-pricing для скидок, второй
 *     запрос не нужен.
 */
import { useEffect, useState } from 'react'
import { getModelPricing } from '../api/chat'
import type { FreeTierParams, ModelPricing } from '../api/chat'

interface Props {
	provider?: string | null
	model?: string | null
	/** Тракт суточного счётчика; null/undefined без `data` = не рендерить
	 *  (аудио/презентации бесплатного тира не имеют) */
	type?: 'chat' | 'image' | 'video' | null
	/** Ценообразующие параметры выбранной комбинации (резолюция, длительность…) */
	params?: FreeTierParams | null
	/** Любое изменение значения = перезагрузить остаток (виджеты передают
	 *  флаг генерации: смена на старте и завершении обновляет счётчик) */
	refreshKey?: unknown
	/** Готовый ответ model-pricing — компонент ничего не грузит сам */
	data?: ModelPricing | null
	className?: string
}

export default function FreeTierBadge({
	provider,
	model,
	type,
	params,
	refreshKey,
	data,
	className,
}: Props) {
	const external = data !== undefined
	const [loaded, setLoaded] = useState<ModelPricing | null>(null)
	// Стабильный ключ параметров: объект params пересоздаётся на каждый рендер
	const paramsSig = JSON.stringify(params ?? {})
	useEffect(() => {
		if (external) return
		if (!provider || !model || !type) {
			setLoaded(null)
			return
		}
		let alive = true
		getModelPricing(provider, model, type, params).then((r) => {
			if (alive) setLoaded(r)
		})
		return () => {
			alive = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [external, provider, model, type, paramsSig, refreshKey])

	const info = external ? data : loaded
	if (!info?.has_free || info.free_limit == null || info.free_limit <= 0) return null
	const left = info.free_remaining ?? 0
	const resetTime = info.free_resets_at
		? new Date(info.free_resets_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
		: null
	return (
		<div className={`free-tier-badge${left > 0 ? '' : ' free-tier-badge--empty'}${className ? ` ${className}` : ''}`}>
			{left > 0 ? (
				<>
					Бесплатно сегодня: {left} из {info.free_limit}
					{resetTime && <span className="free-tier-badge__reset"> · обновится в {resetTime}</span>}
				</>
			) : (
				<>
					Бесплатные на сегодня закончились
					{resetTime && <span className="free-tier-badge__reset"> · обновятся в {resetTime}</span>}
				</>
			)}
		</div>
	)
}
